import type { CalendarAvailability, CalendarEvent, UserPreferences } from 'paintscout'
import moment from 'moment-timezone'

const DAY_START = '12:00am'
const DAY_END = '11:59pm'

// Gaps are built in *browser-local* time. The sales calendar relies on that:
// `shiftEventsToCompanyTz` and `computeGridBounds` (apps/app Calendar/calendarUtils.ts)
// leave `_unavailable_` events unshifted. Change both if this changes.
function parseTime(timeStr: string, baseDate: moment.Moment) {
  return moment(baseDate.format('YYYY-MM-DD') + ' ' + timeStr, 'YYYY-MM-DD hh:mma')
}

function getDayGaps(dayAvailability: { start: string; end: string }[], date: Date): CalendarEvent[] {
  const baseDate = moment(date)
  const startOfDay = parseTime(DAY_START, baseDate)
  const endOfDay = parseTime(DAY_END, baseDate)

  // Sort and normalize ranges
  const ranges = [...dayAvailability]
    .map((r) => ({
      start: parseTime(r.start, baseDate),
      end: parseTime(r.end, baseDate)
    }))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf())

  const gaps: CalendarEvent[] = []
  let lastEnd = startOfDay.clone()

  for (const range of ranges) {
    if (range.start.isAfter(lastEnd)) {
      // Gap between lastEnd and range.start
      gaps.push({
        id: `gap-${lastEnd.toISOString()}-${range.start.toISOString()}`,
        start: lastEnd.toDate(),
        end: range.start.toDate(),
        eventType: '_unavailable_'
      })
    }
    lastEnd = moment.max(lastEnd, range.end)
  }

  if (lastEnd.isBefore(endOfDay)) {
    // Gap from lastEnd to endOfDay
    gaps.push({
      id: `gap-${lastEnd.toISOString()}-${endOfDay.toISOString()}`,
      start: lastEnd.toDate(),
      end: endOfDay.toDate(),
      eventType: '_unavailable_'
    })
  }

  return gaps
}

export function getAvailabilityForDate(availability: CalendarAvailability, date: Date, timezone?: string) {
  // When timezone is provided, extract the calendar date string and reconstruct in the company timezone
  // so that day-of-week, override matching, and time formatting are all correct
  const dateMoment = timezone ? moment.tz(moment(date).format('YYYY-MM-DD'), 'YYYY-MM-DD', timezone) : moment(date)

  // Check for overrides array (array of {start, end} or {unavailable: true})
  if (Array.isArray(availability.overrides)) {
    // Find all overrides that match this date
    const overrides = availability.overrides.filter((o: any) => {
      if (!o.start || !o.end) {
        return false
      }
      const start = timezone ? moment(o.start).tz(timezone) : moment(o.start)
      return dateMoment.isSame(start, 'day')
    })

    // Otherwise, use all time ranges from overrides for this day
    if (overrides.length > 0) {
      return overrides.map((o: any) => ({
        start: timezone ? moment(o.start).tz(timezone).format('hh:mma') : moment(o.start).format('hh:mma'),
        end: timezone ? moment(o.end).tz(timezone).format('hh:mma') : moment(o.end).format('hh:mma')
      }))
    }
  }
  // Fallback to day-of-week
  const dayOfWeek = dateMoment.format('dddd').toLowerCase()
  return availability[dayOfWeek] || []
}
export function getAvailabilityGaps({
  availability,
  date,
  view
}: {
  availability: UserPreferences['availability']
  date: Date
  view: 'week' | 'day'
}): CalendarEvent[] {
  if (!availability) return []

  if (view === 'day') {
    const dayAvailability = getAvailabilityForDate(availability, date)
    return getDayGaps(dayAvailability, date)
  }

  // view === 'week'
  const weekStart = moment(date).startOf('week') // Sunday
  const gaps: CalendarEvent[] = []
  for (let i = 0; i < 7; i++) {
    const day = weekStart.clone().add(i, 'days')
    const dayAvailability = getAvailabilityForDate(availability, day.toDate())
    gaps.push(...getDayGaps(dayAvailability, day.toDate()))
  }
  return gaps
}
